import {
    createContext,
    useState,
    useContext,
    PropsWithChildren,
    useRef,
    useEffect,
    useCallback,
    useMemo
} from "react";

import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useRovoEnabledCohort } from "@/components/user-context/helpers/cohorts";

export interface Source {
    title: string;
    url: string;
}

export type AnonAuthTokenResponse = {
    hasAccess: boolean;
    anonAuthToken: string;
};

export type FeedbackState = "LIKE" | "DISLIKE";

export const csmAiBaseUrl =
    process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"] ||
    "https://www.atlassian.com/gateway/api";
export const cloudId =
    process.env["SEARCH_AI_SUMMARY_CLOUD_SITE_ID"] ||
    "b269ef88-2e2b-44d1-b489-75e6cb57fa1e";
export const agentId =
    process.env["SEARCH_AI_SUMMARY_AGENT_ID"] ||
    "0ccfeb90-6bf6-3e47-a015-c937edcdbe27";
export const helpCenterAri =
    process.env["SEARCH_AI_SUMMARY_HELP_CENTER_ARI"] ||
    "ari:cloud:help::help-center/6fe401af-9648-48b0-bd2d-9e9e572f7a26/d52415c9-03cc-4853-b033-faeb1427c757";

export type AISummaryContextType = {
    summary: string;
    sources: Source[] | undefined;
    conversationId: string;
    messageId: string;
    isLoading: boolean;
    traceResponse: string | undefined;
    likeFeedback: () => void;
    dislikeFeedback: () => void;
    isRovoEnabledCohort: boolean | undefined;
    query: string;
};

export const AISummaryContext = createContext<AISummaryContextType | null>(
    null
);

export const useAISummaryContext = (): AISummaryContextType => {
    const context = useContext(AISummaryContext);
    if (!context) {
        throw new Error(
            "useAISummaryContext must be used within a AISummaryContextProvider"
        );
    }
    return context;
};

interface AISummaryContextProviderProps extends PropsWithChildren {
    query: string;
}

export const AISummaryContextProvider = ({
    query,
    children
}: AISummaryContextProviderProps) => {
    const [summary, setSummary] = useState<string>("");
    const [sources, setSources] = useState<Source[]>();
    const [conversationId, setConversationId] = useState<string>("");
    const [messageId, setMessageId] = useState<string>("");
    const [isLoading, setIsLoading] = useState(true);
    const [traceResponse, setTraceResponse] = useState<string | undefined>(
        undefined
    );
    const [anonAuthToken, setAnonAuthToken] = useState<string>("");

    const abortControllerRef = useRef<AbortController | null>(null);

    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();
    const isRovoEnabledCohort = useRovoEnabledCohort();

    const processLine = useCallback(
        // eslint-disable-next-line complexity
        (line: string) => {
            try {
                const parsed = JSON.parse(line);
                if (parsed.type === "TRACE" && parsed.message?.content) {
                    // TRACE contains the initial loading messages
                    let traceContent = parsed.message.content;

                    if (traceContent.includes(": ")) {
                        traceContent = traceContent.split(": ")[1];
                    }
                    if (traceContent.endsWith(".")) {
                        traceContent = traceContent.slice(0, -1);
                    }
                    setTraceResponse(traceContent);
                } else if (
                    parsed.type === "FINAL_RESPONSE" &&
                    (parsed.message?.sources || parsed.message?.message.sources)
                ) {
                    // FINAL_RESPONSE contains the entire summary, sources, and additional info LLM info
                    const sourcesData = (parsed.message?.sources ||
                        parsed.message?.message?.sources) as Source[];

                    if (!sourcesData || sourcesData.length === 0) {
                        createAnalyticsEvent({
                            eventName: "searchAiSummaryNoSources",
                            eventType: "track",
                            action: "failed",
                            actionSubject: "searchAiSummary",
                            actionSubjectId:
                                "searchAiSummaryFinalResponseLoaded",
                            source: "searchPage",
                            attributes: {
                                query: query,
                                conversationId: conversationId
                            }
                        }).fire();
                    }

                    console.log("parsed", parsed);
                    if (parsed.message?.message?.id) {
                        setMessageId(parsed.message?.message?.id);
                    }

                    setSources(sourcesData);
                    setTraceResponse(undefined);
                } else if (
                    parsed.type === "ANSWER_PART" &&
                    parsed.message?.content
                ) {
                    setSummary((prev) => prev + parsed.message.content);
                    setTraceResponse(undefined);
                }
            } catch (error) {
                console.warn("Failed to parse line as JSON:", line);
                setSummary((prev) => prev + "\n" + line);
            }
        },
        [createAnalyticsEvent, query, conversationId]
    );

    // eslint-disable-next-line complexity
    const fetchAISummary = useCallback(async () => {
        setIsLoading(true);
        setTraceResponse(undefined);
        setSources(undefined);
        setSummary("");
        setMessageId("");
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        // Generate a new conversation ID for each new query
        const newConversationId = uuidv4();
        setConversationId(newConversationId);

        const userContext = {
            user_locale: intl.locale || "en",
            current_time: new Date().toISOString()
        };
        const queryWithUserContext = `${query} \n ${JSON.stringify(
            userContext
        )}`;

        let reader = null;
        try {
            const response = await fetch(
                `${csmAiBaseUrl}/assist/csm/v2/chat/channel/${newConversationId}/message/stream`,
                {
                    method: "POST",
                    credentials: "omit",
                    signal: abortControllerRef.current?.signal,
                    headers: {
                        "x-cloudid": cloudId,
                        "x-product": "csm",
                        "x-experience-id": "csm-ai",
                        "x-anonymous-auth": anonAuthToken,
                        "content-type": "application/json;charset=UTF-8"
                    },
                    body: JSON.stringify({
                        content: {
                            version: 1,
                            type: "doc",
                            content: [
                                {
                                    type: "paragraph",
                                    content: [
                                        {
                                            type: "text",
                                            text: queryWithUserContext
                                        }
                                    ]
                                }
                            ]
                        },
                        recipient_agent_named_id: "atlassian_wac_agent",
                        agent_id: agentId,
                        mimeType: "text/adf",
                        store_message: false,
                        citations_enabled: true
                    })
                }
            );

            createAnalyticsEvent({
                eventName: "aiSummary",
                eventType: "track",
                action: "loaded",
                actionSubject: "searchAiSummary",
                actionSubjectId: "searchAiSummaryLoaded",
                source: "searchPage",
                eventContainer: "load",
                attributes: {
                    responseCode: response.status,
                    responseBodyAvailable: response.body ? true : false
                }
            }).fire();

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) {
                throw new Error("Response body is not available for streaming");
            }

            reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    if (buffer.trim()) {
                        processLine(buffer.trim());
                        setIsLoading(false);
                    }
                    break;
                }

                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;

                const lines = buffer.split("\n");
                buffer = lines.pop() || "";

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (trimmedLine) {
                        processLine(trimmedLine);
                        setIsLoading(false);
                    }
                }
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }
            console.error("Error processing AI summary stream:", error);
            setSummary(
                (prev) =>
                    prev +
                    "\nAtlassian Intelligence can't provide a response right now."
            );
        } finally {
            reader?.releaseLock();
            setIsLoading(false);
        }
    }, [processLine, anonAuthToken, query, intl.locale, createAnalyticsEvent]);

    const getAnonAuthToken = async () => {
        const response = await fetch(
            `${csmAiBaseUrl}/assist/csm/v2/widgets/access?helpCenterAri=${helpCenterAri}`,
            {
                method: "GET",
                credentials: "omit",
                headers: {
                    "x-cloudid": cloudId,
                    "x-product": "csm",
                    "x-experience-id": "csm-ai",
                    "content-type": "application/json;charset=UTF-8"
                }
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = (await response.json()) as AnonAuthTokenResponse;

        if (!data.hasAccess) {
            throw new Error("User does not have access to the help center");
        }

        setAnonAuthToken(data.anonAuthToken);
    };

    const sendFeedback = useCallback(
        (feedbackState: FeedbackState) => {
            return async () => {
                if (!anonAuthToken || !messageId || !conversationId) {
                    return;
                }

                fetch(
                    `${csmAiBaseUrl}/assist/csm/v2/chat/channel/${conversationId}/message/${messageId}/feedback`,
                    {
                        method: "PUT",
                        credentials: "omit",
                        headers: {
                            "Content-Type": "application/json",
                            "x-cloudid": cloudId,
                            "x-product": "csm",
                            "x-experience-id": "csm-ai",
                            "x-anonymous-auth": anonAuthToken
                        },
                        body: JSON.stringify({
                            feedback_value: feedbackState,
                            message_id: messageId,
                            conversation_channel_id: conversationId
                        })
                    }
                ).catch((error) => {
                    console.error("Failed to send feedback:", error);
                });
            };
        },
        [anonAuthToken, messageId, conversationId]
    );

    useEffect(() => {
        getAnonAuthToken().catch((error) => {
            console.error("Error getting anonymous auth token:", error);
        });
    }, []);

    useEffect(() => {
        if (anonAuthToken) {
            fetchAISummary().catch((error) => {
                console.error("Error fetching AI summary:", error);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, anonAuthToken]);

    const contextValue = useMemo(
        () => ({
            summary,
            sources,
            conversationId,
            messageId,
            isLoading,
            traceResponse,
            likeFeedback: sendFeedback("LIKE"),
            dislikeFeedback: sendFeedback("DISLIKE"),
            isRovoEnabledCohort,
            query
        }),
        [
            summary,
            sources,
            conversationId,
            messageId,
            isLoading,
            traceResponse,
            sendFeedback,
            isRovoEnabledCohort,
            query
        ]
    );

    return (
        <AISummaryContext.Provider value={contextValue}>
            {children}
        </AISummaryContext.Provider>
    );
};
